import {
  NAV_FOCUS_TAB_ID,
  type NavTab,
  type RecentTabEntry,
} from 'kci-design-lib';

export const TAB_MRU_LIMIT = 5;
export const RECENT_TAB_LIMIT = 10;

export interface TabState {
  tabsById: Readonly<Record<string, NavTab>>;
  tabOrder: readonly string[];
  activeTabId: string;
  previousActiveTabId: string | null;
  mruTabIds: readonly string[];
  recentTabs: readonly RecentTabEntry[];
  collapsedGroupIds: readonly string[];
  revision: number;
  hydrationStatus: 'idle' | 'loading' | 'ready' | 'error';
}

export type TabAction =
  | { type: 'HYDRATION_STARTED' }
  | { type: 'HYDRATION_FAILED' }
  | { type: 'HYDRATE'; payload: { tabs: readonly NavTab[]; activeTabId?: string | null; recentTabs?: readonly RecentTabEntry[] } }
  | { type: 'REPLACE_TABS'; payload: { tabs: readonly NavTab[] } }
  | { type: 'SET_ACTIVE_TAB'; payload: { tabId: string } }
  | { type: 'SET_COLLAPSED_GROUPS'; payload: { groupIds: readonly string[] } }
  | { type: 'OPEN_OR_ACTIVATE'; payload: { tab: NavTab; trackFocus?: boolean } }
  | { type: 'ACTIVATE'; payload: { tabId: string; trackFocus?: boolean } }
  | { type: 'CLOSE'; payload: { tabId: string } }
  | { type: 'CLOSE_ALL_REMOVABLE' }
  | { type: 'UPDATE_TAB'; payload: { tabId: string; changes: Partial<Omit<NavTab, 'id'>> } }
  | { type: 'REPLACE_RECENT'; payload: { entries: readonly RecentTabEntry[] } }
  | { type: 'UPDATE_RECENT'; payload: { entryId: string; changes: Partial<Omit<RecentTabEntry, 'id'>> } }
  | { type: 'TOGGLE_RECENT_PIN'; payload: { entryId: string; pinned: boolean; pinnedAt?: number } }
  | { type: 'TOGGLE_GROUP_COLLAPSE'; payload: { groupId: string } };

export function createInitialTabState(focusTitle: string): TabState {
  const focusTab: NavTab = {
    id: NAV_FOCUS_TAB_ID,
    label: 'Focus',
    labelKey: 'nav.focus',
    removable: false,
    tabKind: 'chats',
    context: { kind: 'chat', sessionId: null, title: focusTitle },
  };

  return {
    tabsById: { [focusTab.id]: focusTab },
    tabOrder: [focusTab.id],
    activeTabId: focusTab.id,
    previousActiveTabId: null,
    mruTabIds: [],
    recentTabs: [],
    collapsedGroupIds: [],
    revision: 0,
    hydrationStatus: 'idle',
  };
}

function uniqueTabs(tabs: readonly NavTab[]): NavTab[] {
  const byId = new Map<string, NavTab>();
  tabs.forEach((tab) => byId.set(tab.id, tab));
  return [...byId.values()];
}

function withRevision(state: TabState, changes: Partial<TabState>): TabState {
  return { ...state, ...changes, revision: state.revision + 1 };
}

function nextMru(tabId: string, current: readonly string[], validIds: ReadonlySet<string>): string[] {
  return [tabId, ...current.filter((id) => id !== tabId)]
    .filter((id) => validIds.has(id))
    .slice(0, TAB_MRU_LIMIT);
}

function validCollapsedGroups(tabs: readonly NavTab[], groupIds: readonly string[]): string[] {
  const validIds = new Set(
    tabs.map((tab) => tab.clientId).filter((id): id is string => Boolean(id)),
  );
  return groupIds.filter((id) => validIds.has(id));
}

function chooseCloseFallback(
  state: TabState,
  closingIndex: number,
  remainingOrder: readonly string[],
): string {
  const validIds = new Set(remainingOrder);
  if (state.previousActiveTabId && validIds.has(state.previousActiveTabId)) {
    return state.previousActiveTabId;
  }

  const mruFallback = state.mruTabIds.find((id) => validIds.has(id));
  if (mruFallback) return mruFallback;

  return remainingOrder[Math.max(0, Math.min(closingIndex - 1, remainingOrder.length - 1))]
    ?? NAV_FOCUS_TAB_ID;
}

export function tabReducer(state: TabState, action: TabAction): TabState {
  switch (action.type) {
    case 'HYDRATION_STARTED':
      return { ...state, hydrationStatus: 'loading' };
    case 'HYDRATION_FAILED':
      return { ...state, hydrationStatus: 'error' };
    case 'HYDRATE': {
      const focusTab = state.tabsById[NAV_FOCUS_TAB_ID];
      if (!focusTab) return state;

      const tabs = uniqueTabs([focusTab, ...action.payload.tabs.filter((tab) => tab.id !== NAV_FOCUS_TAB_ID)]);
      const tabsById = Object.fromEntries(tabs.map((tab) => [tab.id, tab]));
      const requestedActiveId = action.payload.activeTabId ?? state.activeTabId;
      const activeTabId = tabsById[requestedActiveId] ? requestedActiveId : NAV_FOCUS_TAB_ID;
      return withRevision(state, {
        tabsById,
        tabOrder: tabs.map((tab) => tab.id),
        activeTabId,
        previousActiveTabId: null,
        mruTabIds: activeTabId === NAV_FOCUS_TAB_ID ? [] : [activeTabId],
        recentTabs: [...(action.payload.recentTabs ?? state.recentTabs)].slice(0, RECENT_TAB_LIMIT),
        collapsedGroupIds: validCollapsedGroups(tabs, state.collapsedGroupIds),
        hydrationStatus: 'ready',
      });
    }
    case 'REPLACE_TABS': {
      const focusTab = state.tabsById[NAV_FOCUS_TAB_ID];
      if (!focusTab) return state;
      const tabs = uniqueTabs([focusTab, ...action.payload.tabs.filter((tab) => tab.id !== NAV_FOCUS_TAB_ID)]);
      const tabsById = Object.fromEntries(tabs.map((tab) => [tab.id, tab]));
      const activeTabId = tabsById[state.activeTabId] ? state.activeTabId : NAV_FOCUS_TAB_ID;
      return withRevision(state, {
        tabsById,
        tabOrder: tabs.map((tab) => tab.id),
        activeTabId,
        mruTabIds: state.mruTabIds.filter((id) => Boolean(tabsById[id])).slice(0, TAB_MRU_LIMIT),
        collapsedGroupIds: validCollapsedGroups(tabs, state.collapsedGroupIds),
      });
    }
    case 'SET_ACTIVE_TAB':
      return state.tabsById[action.payload.tabId]
        ? withRevision(state, { activeTabId: action.payload.tabId })
        : state;
    case 'SET_COLLAPSED_GROUPS': {
      const tabs = state.tabOrder
        .map((id) => state.tabsById[id])
        .filter((tab): tab is NavTab => Boolean(tab));
      return withRevision(state, {
        collapsedGroupIds: validCollapsedGroups(tabs, [...new Set(action.payload.groupIds)]),
      });
    }
    case 'OPEN_OR_ACTIVATE': {
      const existing = state.tabsById[action.payload.tab.id];
      const tab = existing ? { ...existing, ...action.payload.tab } : action.payload.tab;
      const tabOrder = existing ? state.tabOrder : [...state.tabOrder, tab.id];
      const validIds = new Set(tabOrder);
      return withRevision(state, {
        tabsById: { ...state.tabsById, [tab.id]: tab },
        tabOrder,
        previousActiveTabId: state.activeTabId === tab.id ? state.previousActiveTabId : state.activeTabId,
        activeTabId: tab.id,
        mruTabIds: action.payload.trackFocus === false
          ? [...state.mruTabIds]
          : nextMru(tab.id, state.mruTabIds, validIds),
      });
    }
    case 'ACTIVATE': {
      if (!state.tabsById[action.payload.tabId]) return state;
      const isSameTab = state.activeTabId === action.payload.tabId;
      return withRevision(state, {
        previousActiveTabId: isSameTab ? state.previousActiveTabId : state.activeTabId,
        activeTabId: action.payload.tabId,
        mruTabIds: action.payload.trackFocus === false
          ? [...state.mruTabIds]
          : nextMru(action.payload.tabId, state.mruTabIds, new Set(state.tabOrder)),
      });
    }
    case 'CLOSE': {
      const closingTab = state.tabsById[action.payload.tabId];
      if (!closingTab || !closingTab.removable) return state;

      const closingIndex = state.tabOrder.indexOf(closingTab.id);
      const tabOrder = state.tabOrder.filter((id) => id !== closingTab.id);
      const { [closingTab.id]: _closedTab, ...tabsById } = state.tabsById;
      const wasActive = state.activeTabId === closingTab.id;
      const activeTabId = wasActive
        ? chooseCloseFallback(state, closingIndex, tabOrder)
        : state.activeTabId;
      const tabs = tabOrder.map((id) => tabsById[id]).filter((tab): tab is NavTab => Boolean(tab));
      return withRevision(state, {
        tabsById,
        tabOrder,
        activeTabId,
        previousActiveTabId: wasActive ? closingTab.id : state.previousActiveTabId,
        mruTabIds: state.mruTabIds.filter((id) => id !== closingTab.id && Boolean(tabsById[id])),
        collapsedGroupIds: validCollapsedGroups(tabs, state.collapsedGroupIds),
      });
    }
    case 'CLOSE_ALL_REMOVABLE': {
      const tabOrder = state.tabOrder.filter((id) => state.tabsById[id]?.removable === false);
      const tabsById = Object.fromEntries(tabOrder.map((id) => [id, state.tabsById[id]]));
      return withRevision(state, {
        tabsById,
        tabOrder,
        activeTabId: NAV_FOCUS_TAB_ID,
        previousActiveTabId: state.activeTabId,
        mruTabIds: [],
        collapsedGroupIds: [],
      });
    }
    case 'UPDATE_TAB': {
      const tab = state.tabsById[action.payload.tabId];
      if (!tab) return state;
      return withRevision(state, {
        tabsById: {
          ...state.tabsById,
          [tab.id]: { ...tab, ...action.payload.changes, id: tab.id },
        },
      });
    }
    case 'REPLACE_RECENT':
      return withRevision(state, { recentTabs: [...action.payload.entries].slice(0, RECENT_TAB_LIMIT) });
    case 'UPDATE_RECENT': {
      if (!state.recentTabs.some((entry) => entry.id === action.payload.entryId)) return state;
      return withRevision(state, {
        recentTabs: state.recentTabs.map((entry) =>
          entry.id === action.payload.entryId ? { ...entry, ...action.payload.changes, id: entry.id } : entry,
        ),
      });
    }
    case 'TOGGLE_RECENT_PIN':
      return tabReducer(state, {
        type: 'UPDATE_RECENT',
        payload: {
          entryId: action.payload.entryId,
          changes: {
            isPinned: action.payload.pinned,
            pinnedAt: action.payload.pinned ? action.payload.pinnedAt ?? Date.now() : null,
          },
        },
      });
    case 'TOGGLE_GROUP_COLLAPSE': {
      const groupId = action.payload.groupId;
      const validGroup = state.tabOrder.some((id) => state.tabsById[id]?.clientId === groupId);
      if (!validGroup) return state;
      return withRevision(state, {
        collapsedGroupIds: state.collapsedGroupIds.includes(groupId)
          ? state.collapsedGroupIds.filter((id) => id !== groupId)
          : [...state.collapsedGroupIds, groupId],
      });
    }
  }
}